import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Col, Icon, message, Row, Spin, Tooltip
} from 'antd';
import AlertMessage from 'components/alertMessage/alert-message';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import _ from 'lodash';

/**
 * FormWrapper
 * @description Higher Order Component for FormInput
 * @version 1.0.1
 */
const FormWrapper = (WrappedComponent) => {
  // default input layout
  const inputFormItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
    }
  };
  // default checkbox layout
  const checkboxFormItemLayout = {
    wrapperCol: {
      xs: {
        span: 24,
        offset: 0
      },
      sm: {
        span: 16,
        offset: 8
      }
    }
  };
  /**
   * @description: the initial form state currentItem {object}, isFormDirty {bool}
   */
  const getInitialFormState = () => ({
    currentItem: {},
    isFormDirty: false
  });
  /**
   * @description Omit propterties from compare
   * @param {data} object
   * @param {props} array
   * @return {_.omit} object
   */
  const omit = (data = {}, props = ['created_date', 'modified_date', 'delete_date']) => _.omit(data, props);

  /**
   * @description Higher Ordered Component
   * @return HOC
   */
  class HOC extends Component {
    constructor(props) {
      super(props);

      this.state = getInitialFormState();
      this.handleSaveClick = this.handleSaveClick.bind(this);
      this.handleValuesChange = this.handleValuesChange.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);

      this.getFormRef = this.getFormRef.bind(this);
    }

    componentDidMount() {
      const { dataSource } = this.props;
      this.resetFormState(dataSource);
    }

    shouldComponentUpdate(nextProps) {
      const {
        dataSource, parent, onFormClose, formError
      } = this.props;
      const { currentItem, isFormDirty } = this.state;
      const objectChanged = !_.isEqual(omit(dataSource), omit(nextProps.dataSource)); // handle the new selected item dataSource
      // Null-safe: an unexpected error shape (e.g. a 5xx surfaced by the axios interceptor as
      // Error('Server error'), which has no allErrors) must never throw here and blank the page.
      const nextAllErrors = _.get(nextProps, 'formError.allErrors', []);
      const currentAllErrors = _.get(formError, 'allErrors', []);
      const hasFormErrors = nextAllErrors.length > 0 && !currentAllErrors.length;

      if (hasFormErrors) return false; // stop update if errors present

      const reloadObject = isFormDirty && nextProps.dataSource.id !== dataSource.id;
      const parentChange = !_.isEqual(parent, nextProps.parent);

      // only process when data not in flight
      if (!nextProps.inFlight) {
        if (objectChanged && reloadObject && !hasFormErrors && dataSource.id > 0 && nextProps.dataSource.id > 0) {
          this.resetFormState(nextProps.dataSource);
          onFormClose(nextProps.dataSource, currentItem);
          return false;
        }
        if (parentChange && dataSource.id && isFormDirty) {
          this.resetFormState(nextProps.dataSource);
          onFormClose(nextProps.dataSource, currentItem);
          return false;
        }

        if (objectChanged && !hasFormErrors) {
          this.resetFormState(nextProps.dataSource);
        }
        else if (reloadObject && !hasFormErrors) {
          this.resetFormState(dataSource);
        }
        else if (isFormDirty && parentChange) {
          this.resetFormState(nextProps.dataSource);
        }
      }

      return true;
    }

    componentWillUnmount() {
      const { currentItem, isFormDirty } = this.state;
      const { dataSource, onFormClose } = this.props;
      if (isFormDirty) {
        onFormClose(dataSource, currentItem);
      }
    }

    /**
     * @description Assign the form reference
     * @param {form} Object
     */
    getFormRef(form) {
      this.formInput = form || this.formInput;
    }

    /**
     * @description Get the messages for related flags
     * @renders {AlertMessage}
     */
    getMessaging() {
      const { dataSource, messages } = this.props;
      const foundKeys = Object.keys(dataSource)
        .filter(key => dataSource[key] && messages[key]);
      const activeMessages = foundKeys.map(key => messages[key]); // the value is true and the message exists
      return (
        <AlertMessage
          banner
          showIcon
          type="info"
          title="Information"
          messageList={activeMessages}
        />
      );
    }

    /**
     * @description Update the currentItem state with changed field values
     * and set the form as dirty
     * @param {values} Object
     * @triggers setState
     */
    handleValuesChange(values) {
      this.setState(prevState => ({
        currentItem: {
          ...prevState.currentItem,
          ...values
        },
        isFormDirty: true
      }));
    }

    /**
     * @description Cancel the current state
     * @triggers {onCancelClick}
     */
    handleCloseClick() {
      const { onCancelClick } = this.props;
      this.setState({
        isFormDirty: false
      }, () => onCancelClick());
    }

    /**
     * @description Validate the form after attempt to save
     * @triggers {onSaveClick}
     */
    handleSaveClick() {
      const { onSaveClick, parent } = this.props;
      const { currentItem, isFormDirty } = this.state;
      const { id } = currentItem;
      const isNewItem = id < 0;

      if (this.formInput) {
        if (!isFormDirty && !isNewItem) {
          // Data has not changed. No need to save
          message.info('There are no changes to be saved.');
        }
        else {
          // Validate and Save if no errors
          this.formInput.validateFields((error, values) => {
            if (!error) {
              // check to see if a legimitate server response is returned
              Promise.resolve(onSaveClick(id, { ...currentItem, ...values, ...parent }))
                .then(response => this.setState({
                  isFormDirty: response && response.allErrors // form not dirty anymore
                })); // save the form data
            }
          });
        }
      }
    }

    /**
     * @description Assign the current dataSource to setState
     * and set the form state to false
     * @param {currentItem} Object
     * @triggers {setState}
     */
    resetFormState(currentItem) {
      this.setState({
        currentItem,
        isFormDirty: false
      });
    }

    /**
     * @description Build the toolbar for the form
     * @param {showSaveButton} bool
     * @param {showCancelButton} bool
     */
    renderToolbar({ showSaveButton, showCancelButton, otherButtons }) {
      const { isEditMode } = this.props;
      const { currentItem, isFormDirty } = this.state;

      return (
        <ToolBar>
          <ToolBar.Right>
            {isEditMode && otherButtons && (
              otherButtons(currentItem)
            )}
            {showSaveButton && (
              <Tooltip title="Save" placement="bottom">
                <Button type="primary" size="small" icon="save" disabled={!isFormDirty} onClick={this.handleSaveClick}>
                  {isEditMode ? 'Save' : 'Add'}
                </Button>
              </Tooltip>
            )}
            {showCancelButton && (
              <Tooltip title="Cancel" placement="bottom">
                <Button type="primary" size="small" icon="close-circle" disabled={!isFormDirty && !isEditMode} onClick={this.handleCloseClick}>
                  Cancel
                </Button>
              </Tooltip>
            )}
          </ToolBar.Right>
        </ToolBar>
      );
    }

    render() {
      const { currentItem } = this.state;
      const {
        showHeader, canAdmin, commands, formError, inFlight, isEditMode, onPopupOk, popupError, title
      } = this.props;
      const buttons = {
        showSaveButton: canAdmin,
        showCancelButton: true,
        ...commands
      };

      const headerTitle = isEditMode ? `Edit ${title}` : `New ${title}`;
      return (
        <div className="bhp-container">
          <Spin indicator={<Icon type="loading" spin />} tip="Loading..." spinning={inFlight}>
            {showHeader && <HeaderBar title={headerTitle} toolbar={this.renderToolbar(buttons)} />}
            <div id={`FormWrapper-${headerTitle}`.replace(/\s+/g, '')} className="bhp-form">
              {this.getMessaging()}
              <WrappedComponent
                ref={this.getFormRef}
                inputFormItemLayout={inputFormItemLayout}
                checkboxFormItemLayout={checkboxFormItemLayout}
                onValuesChange={this.handleValuesChange}
                {...this.props}
                dataSource={currentItem}
              />
            </div>
          </Spin>
          <Row>
            <Col
              xs={{
                offset: 0,
                span: 24
              }}
              sm={{
                offset: 6,
                span: 16
              }}
            >
              <AlertMessage
                size="small"
                type="error"
                title="Error"
                summary="The following errors occured:"
                messageList={_.get(formError, 'allErrors', [])} // Error is only valid when a form is in action
                popUp={popupError}
                onPopUpOk={onPopupOk}
              />
            </Col>
          </Row>
        </div>
      );
    }
  }

  HOC.propTypes = {
    showHeader: PropTypes.bool,
    canAdmin: PropTypes.bool,
    commands: PropTypes.object,
    dataSource: PropTypes.object,
    formError: PropTypes.object,
    inFlight: PropTypes.bool,
    isEditMode: PropTypes.bool,
    messages: PropTypes.object,
    parent: PropTypes.object,
    onSaveClick: PropTypes.func,
    onCancelClick: PropTypes.func,
    onFormClose: PropTypes.func,
    onPopupOk: PropTypes.func,
    popupError: PropTypes.bool,
    title: PropTypes.string
  };

  HOC.defaultProps = {
    showHeader: true,
    canAdmin: false,
    commands: {},
    dataSource: {},
    formError: {
      allErrors: []
    },
    inFlight: false,
    isEditMode: false,
    messages: {},
    parent: null,
    popupError: false,
    onSaveClick() {
    },
    onCancelClick() {
    },
    onFormClose() {
    },
    onPopupOk() {
    },
    title: ''
  };

  return HOC;
};

export default FormWrapper;
